import { styled } from '@mui/material/styles';
import { Box, ButtonBase, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// 회원 카드 hover·focus 때 드러낼 회원 정보 링크의 클래스(카드 스타일이 이 클래스를 찾는다)
export const MEMBER_CARD_LINK_CLASS = 'member-card-link';

// ============================================
// Modal Frame
// ============================================
export const AttendanceModalBox = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '480px',
  maxHeight: '80vh',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const AttendanceModalHeader = styled(Box)({
  backgroundColor: '#fff',
  borderBottom: '1px solid #f0f0f0',
  padding: '16px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
});

export const AttendanceModalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#262626',
});

export const AttendanceModalCloseIcon = styled(CloseIcon)({
  color: '#666',
  cursor: 'pointer',
});

// ============================================
// Body
// ============================================
export const AttendanceModalBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

export const AttendanceTypeBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeColor' && prop !== 'badgeBg',
})(({ badgeColor, badgeBg }) => ({
  display: 'inline-block',
  fontSize: '11px',
  fontWeight: 600,
  color: badgeColor,
  background: badgeBg,
  borderRadius: '4px',
  padding: '2px 8px',
}));

export const AttendanceInfoBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  paddingBottom: '14px',
  marginBottom: '4px',
  borderBottom: '1px solid #f0f0f0',
});

// 일시 + 유형 배지 한 줄 배치 — 배지는 오른쪽 끝에서 살짝 안쪽
export const AttendanceInfoTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  paddingRight: '8px',
});

export const AttendanceInfoDate = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#262626',
});

export const AttendanceInfoTrainer = styled(Typography)({
  fontSize: '13px',
  color: '#666',
});

// ============================================
// Member Card — 상태별 틴트 배경 + 좌측 상태 바 (색+아이콘+텍스트 병행, 텍스트 대비 유지)
// ============================================
const CARD_STATUS_STYLES = {
  RESERVED: { bg: '#F5F9FF', bar: '#007FFF' },
  ATTENDED: { bg: '#EEF7F5', bar: '#0F7F85' },
  ABSENT: { bg: '#FFF3F2', bar: '#EE6A5B' },
};

export const MemberCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'statusKey',
})(({ statusKey }) => {
  const statusStyle = CARD_STATUS_STYLES[statusKey] || { bg: '#FAFBFC', bar: '#d9d9d9' };
  return {
    backgroundColor: statusStyle.bg,
    border: '1px solid #ebebeb',
    borderLeft: `4px solid ${statusStyle.bar}`,
    borderRadius: '10px',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    transition: 'background-color 0.25s ease, border-color 0.25s ease',
    // 카드에 올리거나(마우스) 안의 링크에 포커스가 가면(키보드) 회원 정보 링크를 드러낸다
    '@media (hover: hover) and (pointer: fine)': {
      [`&:hover .${MEMBER_CARD_LINK_CLASS}`]: { opacity: 1, transform: 'none' },
    },
    [`&:focus-within .${MEMBER_CARD_LINK_CLASS}`]: { opacity: 1, transform: 'none' },
  };
});

export const MemberCardRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '13px',
  color: '#595959',
});

// 이름 옆 전화번호 — 이름과 한 덩어리로 보이도록 붙여 놓고 무게만 낮춘다.
export const MemberCardNameGroup = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  minWidth: 0,
});

export const MemberCardPhone = styled('span')({
  fontSize: '12.5px',
  color: '#8A939C',
  whiteSpace: 'nowrap',
});

// 전화번호가 있던 자리 — 차감되는 수강권 이름
export const MemberCardTicket = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const MemberCardName = styled('span')({
  fontSize: '15px',
  fontWeight: 700,
  color: '#262626',
});

// 회원 상세로 가는 링크 — 평소엔 투명(자리는 그대로라 레이아웃이 흔들리지 않음), 카드 hover·focus 때 짧게 미끄러지며 나타난다.
// 터치 기기는 hover 가 없어 항상 보인다. 움직임 줄이기 설정이면 이동 없이 투명도만 바뀐다.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

export const MemberCardLink = styled(ButtonBase)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1px',
  padding: '2px 6px 2px 8px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#4AB3BC',
  whiteSpace: 'nowrap',
  transition: `opacity 160ms ${EASE_OUT}, transform 160ms ${EASE_OUT}, background-color 160ms ease`,
  '& svg': { fontSize: '16px' },
  '&:hover': { backgroundColor: 'rgba(74, 179, 188, 0.12)' },
  '&:focus-visible': { outline: '2px solid #4AB3BC', outlineOffset: '1px' },
  // 마우스처럼 정밀하게 hover 되는 기기에서만 숨겨 두고 카드 hover 로 드러낸다. 그 외(터치·펜 등)는 항상 보인다.
  '@media (hover: hover) and (pointer: fine)': { opacity: 0, transform: 'translateX(-4px)' },
  '@media (prefers-reduced-motion: reduce)': {
    transform: 'none',
    transition: 'opacity 120ms ease, background-color 120ms ease',
  },
});

// 상태 배지 — 채움형 + 아이콘 (색만으로 전달하지 않도록 아이콘·텍스트 병행)
export const MemberCardStatusBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeColor',
})(({ badgeColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#fff',
  backgroundColor: badgeColor,
  borderRadius: '999px',
  padding: '3px 10px',
  whiteSpace: 'nowrap',
  '& svg': { fontSize: '14px' },
}));

// ============================================
// 세션 잔여 강조 블록 — 큰 숫자 + 진행 바
// ============================================
export const SessionStatBlock = styled(Box)({
  marginTop: '4px',
  paddingTop: '10px',
  borderTop: '1px dashed rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const SessionStatLabel = styled('span')({
  fontSize: '12px',
  fontWeight: 700,
  color: '#404040',
  whiteSpace: 'nowrap',
});

export const SessionStatValue = styled('span', {
  shouldForwardProp: prop => prop !== 'valueColor',
})(({ valueColor = '#0F7F85' }) => ({
  fontSize: '18px',
  fontWeight: 800,
  color: valueColor,
  whiteSpace: 'nowrap',
  '& small': { fontSize: '13px', fontWeight: 700, color: '#404040' },
}));

export const SessionProgressTrack = styled(Box)({
  flex: 1,
  height: '5px',
  borderRadius: '3px',
  backgroundColor: 'rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
});

export const SessionProgressFill = styled(Box, {
  shouldForwardProp: prop => prop !== 'fillPercent' && prop !== 'fillColor',
})(({ fillPercent = 0, fillColor = '#0F7F85' }) => ({
  width: `${Math.min(Math.max(fillPercent, 0), 100)}%`,
  height: '100%',
  borderRadius: '3px',
  backgroundColor: fillColor,
  transition: 'width 0.3s ease',
}));

export const AttendanceDuetNotice = styled(Typography)({
  fontSize: '12px',
  color: '#FF3B30',
  marginTop: '4px',
});

// ============================================
// Footer
// ============================================
export const AttendanceModalFooter = styled(Box)({
  borderTop: '1px solid #f0f0f0',
  padding: '14px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
  flexShrink: 0,
  backgroundColor: '#fff',
});

export const AttendanceActionGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
});

const buttonBase = {
  height: '40px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  padding: '0 16px',
  cursor: 'pointer',
  border: 'none',
};

export const AttendanceCancelButton = styled('button', {
  shouldForwardProp: prop => prop !== 'disabledLook',
})(({ disabledLook }) => ({
  ...buttonBase,
  backgroundColor: '#fff',
  color: '#707070',
  border: '1px solid #d9d9d9',
  ...(disabledLook
    ? { opacity: 0.5, cursor: 'default' }
    : {
        '&:hover': { borderColor: '#FF3B30', color: '#FF3B30' },
      }),
}));

export const AttendanceAbsentButton = styled('button', {
  shouldForwardProp: prop => prop !== 'disabledLook',
})(({ disabledLook }) => ({
  ...buttonBase,
  backgroundColor: '#fff',
  color: '#FF3B30',
  border: '1px solid #FF3B30',
  ...(disabledLook
    ? { opacity: 0.5, cursor: 'default' }
    : {
        '&:hover': { backgroundColor: 'rgba(255, 59, 48, 0.06)' },
      }),
}));

export const AttendanceAttendButton = styled('button', {
  shouldForwardProp: prop => prop !== 'disabledLook',
})(({ disabledLook }) => ({
  ...buttonBase,
  backgroundColor: '#0F7F85',
  color: '#fff',
  ...(disabledLook
    ? { opacity: 0.5, cursor: 'default' }
    : {
        '&:hover': { backgroundColor: '#0d6f74' },
      }),
}));
